import React from 'react';
import { Shield } from 'lucide-react';
import CivicModal from './CivicModal';

interface PrivacyDataModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function PrivacyDataModal({
  isOpen,
  onClose,
}: PrivacyDataModalProps) {
  return (
    <CivicModal
      isOpen={isOpen}
      onClose={onClose}
      title="Privacy & Data"
      icon={Shield}
      maxWidth="lg"
    >
      <div
        className="bg-[#1A1A1A] rounded-xl border"
        style={{
          borderColor: 'rgba(255, 255, 255, 0.08)',
          padding: '32px 24px',
        }}
      >
        <div>
          <h3
            className="font-semibold"
            style={{
              fontSize: '16px',
              color: '#40E0D0',
              marginBottom: '8px',
              letterSpacing: '0.01em',
            }}
          >
            Our Principles
          </h3>
          <p
            className="text-[#E0E0E0]"
            style={{
              fontSize: '14px',
              lineHeight: '1.65',
            }}
          >
            civicbeat is built on trust. We collect only what's necessary, never sell personal data, and design the platform to respect people, stories, and communities.
          </p>
        </div>

        <div style={{ marginTop: '28px' }}>
          <h3
            className="font-semibold"
            style={{
              fontSize: '16px',
              color: '#40E0D0',
              marginBottom: '8px',
              letterSpacing: '0.01em',
            }}
          >
            What We Collect
          </h3>
          <p
            className="text-[#E0E0E0]"
            style={{
              fontSize: '14px',
              lineHeight: '1.65',
              marginBottom: '12px',
            }}
          >
            Right now, civicbeat collects minimal information, such as:
          </p>
          <ul style={{ marginBottom: '12px', paddingLeft: '0' }}>
            <li
              className="text-[#E0E0E0]"
              style={{
                fontSize: '14px',
                lineHeight: '1.65',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '10px',
                marginBottom: '8px',
              }}
            >
              <span style={{ color: '#40E0D0', marginTop: '2px' }}>•</span>
              <span>Account details needed to sign in</span>
            </li>
            <li
              className="text-[#E0E0E0]"
              style={{
                fontSize: '14px',
                lineHeight: '1.65',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '10px',
                marginBottom: '8px',
              }}
            >
              <span style={{ color: '#40E0D0', marginTop: '2px' }}>•</span>
              <span>Preferences you choose (topics you follow, stories you save)</span>
            </li>
            <li
              className="text-[#E0E0E0]"
              style={{
                fontSize: '14px',
                lineHeight: '1.65',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '10px',
              }}
            >
              <span style={{ color: '#40E0D0', marginTop: '2px' }}>•</span>
              <span>Basic, anonymous usage signals that help improve the experience</span>
            </li>
          </ul>
          <p
            className="text-[#E0E0E0]"
            style={{
              fontSize: '14px',
              lineHeight: '1.65',
            }}
          >
            We avoid collecting data that isn't directly useful to you.
          </p>
        </div>

        <div style={{ marginTop: '28px' }}>
          <h3
            className="font-semibold"
            style={{
              fontSize: '16px',
              color: '#40E0D0',
              marginBottom: '8px',
              letterSpacing: '0.01em',
            }}
          >
            How Your Data Is Used
          </h3>
          <p
            className="text-[#E0E0E0]"
            style={{
              fontSize: '14px',
              lineHeight: '1.65',
              marginBottom: '12px',
            }}
          >
            Information is used to:
          </p>
          <ul style={{ marginBottom: '12px', paddingLeft: '0' }}>
            <li
              className="text-[#E0E0E0]"
              style={{
                fontSize: '14px',
                lineHeight: '1.65',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '10px',
                marginBottom: '8px',
              }}
            >
              <span style={{ color: '#40E0D0', marginTop: '2px' }}>•</span>
              <span>Personalise the stories you see</span>
            </li>
            <li
              className="text-[#E0E0E0]"
              style={{
                fontSize: '14px',
                lineHeight: '1.65',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '10px',
                marginBottom: '8px',
              }}
            >
              <span style={{ color: '#40E0D0', marginTop: '2px' }}>•</span>
              <span>Improve civicbeat's design and clarity</span>
            </li>
            <li
              className="text-[#E0E0E0]"
              style={{
                fontSize: '14px',
                lineHeight: '1.65',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '10px',
              }}
            >
              <span style={{ color: '#40E0D0', marginTop: '2px' }}>•</span>
              <span>Understand what resonates — in aggregate, not individually</span>
            </li>
          </ul>
          <p
            className="text-[#E0E0E0]"
            style={{
              fontSize: '14px',
              lineHeight: '1.65',
            }}
          >
            We do not track you across the internet.
          </p>
        </div>

        <div style={{ marginTop: '28px' }}>
          <h3
            className="font-semibold"
            style={{
              fontSize: '16px',
              color: '#40E0D0',
              marginBottom: '8px',
              letterSpacing: '0.01em',
            }}
          >
            Sharing & Collaboration
          </h3>
          <p
            className="text-[#E0E0E0]"
            style={{
              fontSize: '14px',
              lineHeight: '1.65',
              marginBottom: '12px',
            }}
          >
            As civicbeat grows, we may collaborate with educators, researchers, or community partners.
          </p>
          <p
            className="text-[#E0E0E0]"
            style={{
              fontSize: '14px',
              lineHeight: '1.65',
              marginBottom: '12px',
            }}
          >
            If insights are shared, they are:
          </p>
          <ul style={{ marginBottom: '12px', paddingLeft: '0' }}>
            <li
              className="text-[#E0E0E0]"
              style={{
                fontSize: '14px',
                lineHeight: '1.65',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '10px',
                marginBottom: '8px',
              }}
            >
              <span style={{ color: '#40E0D0', marginTop: '2px' }}>•</span>
              <span>Aggregated</span>
            </li>
            <li
              className="text-[#E0E0E0]"
              style={{
                fontSize: '14px',
                lineHeight: '1.65',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '10px',
                marginBottom: '8px',
              }}
            >
              <span style={{ color: '#40E0D0', marginTop: '2px' }}>•</span>
              <span>Anonymised</span>
            </li>
            <li
              className="text-[#E0E0E0]"
              style={{
                fontSize: '14px',
                lineHeight: '1.65',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '10px',
              }}
            >
              <span style={{ color: '#40E0D0', marginTop: '2px' }}>•</span>
              <span>Used for learning, reflection, or public good. Never for targeting or advertising</span>
            </li>
          </ul>
          <p
            className="text-[#E0E0E0]"
            style={{
              fontSize: '14px',
              lineHeight: '1.65',
            }}
          >
            Contributors and collaborators only access what they need to do their work.
          </p>
        </div>

        <div style={{ marginTop: '28px' }}>
          <h3
            className="font-semibold"
            style={{
              fontSize: '16px',
              color: '#40E0D0',
              marginBottom: '8px',
              letterSpacing: '0.01em',
            }}
          >
            Your Control
          </h3>
          <p
            className="text-[#E0E0E0]"
            style={{
              fontSize: '14px',
              lineHeight: '1.65',
              marginBottom: '12px',
            }}
          >
            You stay in control of your relationship with civicbeat.
          </p>
          <p
            className="text-[#E0E0E0]"
            style={{
              fontSize: '14px',
              lineHeight: '1.65',
              marginBottom: '12px',
            }}
          >
            Over time, this will include:
          </p>
          <ul style={{ marginBottom: '12px', paddingLeft: '0' }}>
            <li
              className="text-[#E0E0E0]"
              style={{
                fontSize: '14px',
                lineHeight: '1.65',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '10px',
                marginBottom: '8px',
              }}
            >
              <span style={{ color: '#40E0D0', marginTop: '2px' }}>•</span>
              <span>Managing preferences</span>
            </li>
            <li
              className="text-[#E0E0E0]"
              style={{
                fontSize: '14px',
                lineHeight: '1.65',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '10px',
                marginBottom: '8px',
              }}
            >
              <span style={{ color: '#40E0D0', marginTop: '2px' }}>•</span>
              <span>Exporting or deleting your data</span>
            </li>
            <li
              className="text-[#E0E0E0]"
              style={{
                fontSize: '14px',
                lineHeight: '1.65',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '10px',
              }}
            >
              <span style={{ color: '#40E0D0', marginTop: '2px' }}>•</span>
              <span>Contacting us about privacy or concerns</span>
            </li>
          </ul>
          <p
            className="text-[#E0E0E0]"
            style={{
              fontSize: '14px',
              lineHeight: '1.65',
            }}
          >
            These features are designed intentionally and transparently.
          </p>
        </div>

        <div style={{ marginTop: '28px' }}>
          <h3
            className="font-semibold"
            style={{
              fontSize: '16px',
              color: '#40E0D0',
              marginBottom: '8px',
              letterSpacing: '0.01em',
            }}
          >
            Accountability
          </h3>
          <p
            className="text-[#E0E0E0]"
            style={{
              fontSize: '14px',
              lineHeight: '1.65',
              marginBottom: '12px',
            }}
          >
            Privacy isn't a checkbox for us. It's an ongoing responsibility.
          </p>
          <p
            className="text-[#E0E0E0]"
            style={{
              fontSize: '14px',
              lineHeight: '1.65',
            }}
          >
            If something doesn't feel right, we want to know.
          </p>
        </div>
      </div>
    </CivicModal>
  );
}
